export const streamFromService = async function* (url, payload, options = {}) {
  const {signal, headers = {}} = options;

  const response = await fetch(url, {
    method: "POST",
    headers: {"Content-Type": "application/json", ...headers},
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const {done, value} = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, {stream: true});
      const lines = buffer.split("\n");
      buffer = lines.pop();

      for (const line of lines) {
        if (line.trim()) {
          try {
            yield JSON.parse(line);
          } catch (parseError) {
            console.error("Stream parse error:", parseError);
          }
        }
      }
    }

    if (buffer.trim()) {
      try {
        yield JSON.parse(buffer);
      } catch (parseError) {
        console.error("Final buffer parse error:", parseError);
      }
    }
  } finally {
    reader?.releaseLock();
  }
};

export const STREAM_TIMEOUT_IDLE = "idle";
export const STREAM_TIMEOUT_TOTAL = "total";

// Services emit keepalive chunks every 15s, so an idle window of 60s means four
// consecutive misses. The total cap must stay below the load balancer idle timeout.
export const DEFAULT_IDLE_TIMEOUT_MS = 60000;
export const DEFAULT_TOTAL_TIMEOUT_MS = 240000;

const streamTimeoutError = (reason, ms) => {
  const error = new Error(
    reason === STREAM_TIMEOUT_IDLE ?
      `Stream produced no data for ${ms}ms` :
      `Stream exceeded maximum duration of ${ms}ms`,
  );
  error.name = "StreamTimeoutError";
  error.timeoutReason = reason;
  return error;
};

const isAbortError = (error) => error?.name === "AbortError" || error?.name === "TimeoutError";

const invokeHeartbeat = async (onHeartbeat, chunk) => {
  if (!onHeartbeat) {
    return;
  }
  try {
    await onHeartbeat(chunk);
  } catch (heartbeatError) {
    console.error("Stream heartbeat error:", heartbeatError);
  }
};

// Wraps streamFromService with two independent deadlines:
export const streamWithDeadlines = async function* (url, payload, options = {}) {
  const {
    signal,
    headers = {},
    idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
    totalTimeoutMs = DEFAULT_TOTAL_TIMEOUT_MS,
    onHeartbeat,
  } = options;

  const controller = new AbortController();
  const forwardAbort = () => controller.abort(signal.reason);
  let timeoutReason;
  let idleTimer;

  const armIdleTimer = () => {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      timeoutReason = STREAM_TIMEOUT_IDLE;
      controller.abort();
    }, idleTimeoutMs);
  };

  const totalTimer = setTimeout(() => {
    timeoutReason = STREAM_TIMEOUT_TOTAL;
    controller.abort();
  }, totalTimeoutMs);

  if (signal?.aborted) {
    controller.abort(signal.reason);
  } else {
    signal?.addEventListener("abort", forwardAbort, {once: true});
  }

  armIdleTimer();

  try {
    const stream = streamFromService(url, payload, {signal: controller.signal, headers});

    for await (const chunk of stream) {
      armIdleTimer();

      if (chunk.keepalive) {
        await invokeHeartbeat(onHeartbeat, chunk);
        continue;
      }

      if (chunk.activity) {
        await invokeHeartbeat(onHeartbeat, chunk);
      }

      yield chunk;
    }
  } catch (error) {
    if (timeoutReason && isAbortError(error)) {
      throw streamTimeoutError(
        timeoutReason,
        timeoutReason === STREAM_TIMEOUT_IDLE ? idleTimeoutMs : totalTimeoutMs,
      );
    }
    throw error;
  } finally {
    clearTimeout(idleTimer);
    clearTimeout(totalTimer);
    signal?.removeEventListener("abort", forwardAbort);
  }
};
